import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import ErrorList from "./ErrorList";
import { Box, Typography, IconButton } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { format } from 'date-fns';

const UploadCard = ({
  id,
  key,
  title = "Publication Track",
  uploaded_at,
  onUpdate,
  acceptedFileTypes = ".csv,.xlsx,.xls",
  errors = [],
  isProcessing,
  onProceedWithErrors,
  onIgnoreAndReupload,
  areDependenciesProcessing,
  isSuccess,
  responseFileName,
  isDisabled,
  onIgnoreAndSync,
}) => {

  const fileInputRef = useRef(null);
  const [isErrorListOpen, setIsErrorListOpen] = useState(false);
  const [fileName, setFileName] = useState(responseFileName);
  const hasErrors = errors.length > 0;

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      setFileName(file.name);
      const result = await onUpdate(file, id);
      if (result?.length > 0) {
        setIsErrorListOpen(true);
      }
      event.target.value = "";
    }
  };

  const handleProceed = () => {
    onProceedWithErrors?.();
    setIsErrorListOpen(false);
  };

  const handleIgnore = () => {
    onIgnoreAndSync?.();
    onIgnoreAndReupload?.();
    setIsErrorListOpen(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N.A';
    try {
      const date = new Date(dateString);
      return format(date, 'MM-dd-yyyy:hh:mm a');
    } catch (error) {
      return 'N.A';
    }
  };

  return (
    <div className="flex flex-col max-w-lg min-w-sm">
      <div className={`rounded-lg p-4 ${hasErrors || isDisabled ? 'border border-red-500' : ''}`} style={{ background: '#F9F9F9' }}>
        <div className="flex items-center justify-between">
          <h1 className="font-medium text-lg mb-2">{title}</h1>
          {isProcessing ? (
            <img src="/icons/process.svg" alt="Processing" className="w-6 h-6" />
          ) : isSuccess && (
            <CheckCircleIcon color="success" />
          )}
        </div>
        <div className="text-gray-600 mb-4">{responseFileName ?? fileName ?? "No File Uploaded"}</div>

        <div className="flex items-center gap-4 mb-2">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept={acceptedFileTypes}
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current.click()}
            className={`px-4 py-1 border rounded-md transition-colors ${
              isProcessing || isSuccess || isDisabled
                ? "text-gray-500 border-gray-500 bg-gray-100 cursor-not-allowed"
                : "text-red-500 border-red-500 hover:bg-red-50 transition-colors"
            }`}
            disabled={isProcessing || isSuccess || isDisabled}
          >
            {responseFileName
              ? isProcessing || isSuccess
                ? "Updated"
                : "Update"
              : "Upload"}
          </button>
          <span className="text-gray-500 font-normal text-md">
            Last Upload: <span className="font-semibold">{formatDate(uploaded_at)}</span>
          </span>
        </div>
      </div>

      {(hasErrors || isDisabled) && (
        <div className="flex items-center justify-center text-red-500 mt-1">
          {isDisabled && (
            <img src="/icons/prerequisite.svg" alt="Prerequisite" className="w-4 h-4" />
          )}
          <Typography>
            {isDisabled ? "Prerequisite sheets Unavailable!" : "This sheet has errors."}
          </Typography>
          <IconButton onClick={() => setIsErrorListOpen(true)} size="small">
            {!isDisabled && <InfoIcon color="error" /> }
          </IconButton>
        </div>
      )}

      <ErrorList
        errors={errors}
        open={isErrorListOpen}
        onClose={() => setIsErrorListOpen(false)}
        onProceed={handleProceed}
        onIgnore={handleIgnore}
      />
    </div>
  );
};

UploadCard.propTypes = {
  title: PropTypes.string,
  uploaded_at: PropTypes.string,
  onUpdate: PropTypes.func.isRequired,
  acceptedFileTypes: PropTypes.string,
  errors: PropTypes.array,
  isProcessing: PropTypes.bool,
  isSuccess: PropTypes.bool,
  onProceedWithErrors: PropTypes.func,
  onIgnoreAndReupload: PropTypes.func,
  responseFileName: PropTypes.string,
  isDisabled: PropTypes.bool,
  onIgnoreAndSync: PropTypes.func,
};

export default UploadCard;
